import * as THREE from 'three';
import { LineType, WallType, WallEdgeConnection, JointPolygonData, FillPattern, JointPolygonOutlineSegment, JointFillPolygon } from '../types/floorplannerTypes';
import { JointInfo } from './JointDetector';
import type { FloorplannerStore } from '../store/floorplannerStore';
import { buildJointSolid, multiPolygonToFillPolygons, extractClosingOutline } from './jointGeometry';

// Lazy accessor to avoid circular dependency with floorplannerStore
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStore = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized - jointPolygonGenerator called too early');
    }
  }
  return _floorplannerStore;
};

/**
 * Joint Polygon Generator - Gap-Filling Polygons for Wall Junctions
 *
 * PURPOSE:
 * Generate the gap-filling fill polygons AND the closing outline for a wall
 * junction. Both are derived from the SAME boolean solid (see jointGeometry.ts),
 * so they can never disagree.
 *
 * ALGORITHM (delegated to jointGeometry.buildJointSolid):
 *   footprints         = raw rectangle/arc footprint of each participating wall
 *   renderedFootprints = committed wall-fill footprints after endpoint trimming
 *   cap                = coverage >= 2 of endpoint-extended footprints
 *   S                  = union(footprints, fill)
 *   fill               = difference(cap, renderedFootprints + nearby blockers)
 *   cover              = cap // diagnostic local cap; never rendered as fill
 *   outline            = boundary(S) minus the edges walls draw themselves
 *
 * This replaces the legacy point-cloud + convex-hull approach, which produced
 * order-dependent artifacts (triangular wedges poking outside the wall footprint)
 * because a junction fill region is generally NON-convex.
 *
 * The boolean operations are commutative/associative and depend only on geometry,
 * so the result is identical regardless of the order walls were joined or rendered.
 *
 * @see jointGeometry.ts - boolean CSG core
 * @see JOINT_POLYGON_RENDERING.md
 */
export function createJointPolygon(
  joint: JointInfo,
  walls: Map<string, WallType>
): JointPolygonData | null {
  // Guard against empty connectedWalls
  if (!joint.connectedWalls || joint.connectedWalls.length === 0) {
    return null;
  }

  // Simple straight 2-wall pass-through (collinear / mid T-junction with matching
  // width & style) needs no fill - the walls already tile and outline trimming
  // closes them. Bail out early to avoid generating a zero-area polygon.
  if (isSimpleStraightTwoWallTJoint(joint, walls)) {
    return null;
  }

  // Build the deterministic boolean solid for this joint.
  const solid = buildJointSolid(joint, walls);
  if (!solid) {
    return null;
  }

  // Gap-only fill polygons (already disjoint from every committed wall fill).
  const fillPolygons = multiPolygonToFillPolygons(solid.fill).filter(
    polygon => Math.abs(calculatePolygonArea(polygon.outer)) >= 1e-6
  );
  const coverPolygons = multiPolygonToFillPolygons(solid.cover).filter(
    polygon => Math.abs(calculatePolygonArea(polygon.outer)) >= 1e-6
  );

  // Closing outline edges (miter bridges, width-mismatch steps, flat caps).
  const closingOutline = extractClosingOutline(solid, joint, walls);

  // Nothing to draw at this joint.
  if (fillPolygons.length === 0 && closingOutline.length === 0) {
    return null;
  }

  const isMultiJoint = joint.connectedWalls.length >= 3;
  const wallIds = joint.connectedWalls.map(cw => cw.wallId);

  // Fill color inheritance:
  // - MULTI joints (3+ walls): use the primary wall's properties (unified fill).
  // - 2-wall joints: prefer voidFillMetadata if present, else the first wall.
  let primaryWallId: string | undefined;
  let fillColor: number;
  let fillOpacity: number | undefined;
  let fillPattern: FillPattern | undefined;
  let outlineColor: number | undefined;
  let outlineWidth: number | undefined;
  let outlineDashed: boolean;

  if (isMultiJoint) {
    primaryWallId = selectDeterministicPrimaryWallId(joint, walls);
    const primaryWall = primaryWallId ? walls.get(primaryWallId) : undefined;
    fillColor = primaryWall?.fillColor ?? getFloorplannerStore().fillColor ?? 0xffffff;
    fillOpacity = primaryWall?.fillOpacity;
    fillPattern = primaryWall?.fillPattern;
    outlineColor = primaryWall?.lineColor ?? getFloorplannerStore().lineColor;
    outlineWidth = primaryWall?.lineWeight ?? getFloorplannerStore().wallLineWeight;
    outlineDashed = primaryWall?.lineType === LineType.dashed;
  } else {
    primaryWallId = joint.voidFillMetadata?.ownerWallId || joint.connectedWalls?.[0]?.wallId;
    const primaryWall = primaryWallId ? walls.get(primaryWallId) : undefined;
    fillColor = joint.voidFillMetadata?.fillColor ?? primaryWall?.fillColor ?? 0xffffff;
    fillOpacity = primaryWall?.fillOpacity;
    fillPattern = primaryWall?.fillPattern;
    outlineColor = joint.voidFillMetadata?.outlineColor ?? primaryWall?.lineColor ?? getFloorplannerStore().lineColor;
    outlineWidth = joint.voidFillMetadata?.outlineWidth ?? primaryWall?.lineWeight ?? getFloorplannerStore().wallLineWeight;
    outlineDashed = joint.voidFillMetadata?.outlineDashed ?? (primaryWall?.lineType === LineType.dashed);
  }

  if (!primaryWallId) {
    return null;
  }

  // Collect the edge connections that form this joint (for downstream metadata).
  const edgeConnections: WallEdgeConnection[] = [];
  for (const wallId of wallIds) {
    const wall = walls.get(wallId);
    if (wall?.edgeConnections) {
      const wallConnections = wall.edgeConnections.filter(ec => {
        return wallIds.some(otherWallId => {
          if (otherWallId === wallId) return false;
          return (ec.sourceWallId === wallId && ec.targetWallId === otherWallId) ||
                 (ec.targetWallId === wallId && ec.sourceWallId === otherWallId);
        });
      });
      edgeConnections.push(...wallConnections);
    }
  }

  // Map closing outline edges to styled segments. Each edge inherits the style of
  // its nearest participating wall, falling back to the joint's primary style.
  const fallbackColor = outlineColor ?? getFloorplannerStore().lineColor ?? 0x000000;
  const fallbackWidth = outlineWidth ?? getFloorplannerStore().wallLineWeight;
  const outlineSegments: JointPolygonOutlineSegment[] = closingOutline.map(edge => {
    const ownerWall = edge.wallId ? walls.get(edge.wallId) : undefined;
    return {
      start: edge.start.clone(),
      end: edge.end.clone(),
      color: ownerWall?.lineColor ?? fallbackColor,
      width: ownerWall?.lineWeight ?? fallbackWidth,
      dashed: (ownerWall?.lineType === LineType.dashed) || (!ownerWall && outlineDashed),
      wallId: edge.wallId,
    };
  });

  // Backward-compatible single-ring `vertices` = outer ring of the largest island.
  const largestFill = fillPolygons.reduce<JointFillPolygon | null>((best, polygon) => {
    if (!best) return polygon;
    return Math.abs(calculatePolygonArea(polygon.outer)) > Math.abs(calculatePolygonArea(best.outer))
      ? polygon
      : best;
  }, null);
  const vertices = largestFill ? largestFill.outer.map(point => point.clone()) : [];

  const jointCenter = vertices.length >= 3
    ? calculateJointCenter(vertices)
    : toVector2(joint.point);

  const jointPolygon: JointPolygonData = {
    id: joint.id,
    jointCenter,
    vertices,
    fillPolygons,
    coverPolygons,
    wallIds,
    primaryWallId,
    fillColor,
    fillOpacity,
    fillPattern,
    zIndex: 0.0005, // Between wall fill (z=0) and outlines (z=0.001)
    edgeConnections,
    isVoidFill: !!joint.voidFillMetadata,
    outlineColor,
    outlineWidth,
    outlineDashed,
    outlineSegments: outlineSegments.length > 0 ? outlineSegments : undefined,
    voidFillMetadata: joint.voidFillMetadata,
  };

  return jointPolygon;
}

/**
 * Multi-joint fill ownership must not depend on connection/render order. Use a
 * deterministic geometric rule: widest participating wall wins; ties are stable
 * by wall id. This keeps the dominant wall color consistent across all join
 * permutations.
 */
function selectDeterministicPrimaryWallId(
  joint: JointInfo,
  walls: Map<string, WallType>,
): string | undefined {
  return joint.connectedWalls
    .map(connection => {
      const wall = walls.get(connection.wallId);
      return {
        wallId: connection.wallId,
        width: wall?.wallWidth ?? connection.wall?.wallWidth ?? getFloorplannerStore().wallWidth,
      };
    })
    .sort((a, b) => {
      const widthDelta = b.width - a.width;
      if (Math.abs(widthDelta) > 0.001) {
        return widthDelta;
      }
      return a.wallId.localeCompare(b.wallId);
    })[0]?.wallId;
}

const toVector2 = (point: THREE.Vector2 | { x: number; y: number }): THREE.Vector2 =>
  point instanceof THREE.Vector2 ? point.clone() : new THREE.Vector2(point.x, point.y);

/**
 * Detect a simple straight 2-wall pass-through (collinear, or a wall meeting
 * another's middle) with matching width and line style. Such joints need no fill
 * polygon - they are handled entirely by wall outline trimming.
 */
function isSimpleStraightTwoWallTJoint(
  joint: JointInfo,
  walls: Map<string, WallType>,
): boolean {
  if (joint.connectedWalls.length !== 2 || joint.voidFillMetadata) {
    return false;
  }

  const wallIds = new Set(joint.connectedWalls.map(connection => connection.wallId));
  const hasMiddleConnection =
    joint.connectedWalls.some(connection => connection.connectionPosition === "middle") ||
    joint.connectedWalls.some(connection =>
      walls.get(connection.wallId)?.edgeConnections?.some(edgeConnection =>
        wallIds.has(edgeConnection.sourceWallId) &&
        wallIds.has(edgeConnection.targetWallId) &&
        edgeConnection.targetEndpoint === "middle"
      ) ?? false
    );
  if (!hasMiddleConnection) {
    return false;
  }

  const participatingWalls = joint.connectedWalls
    .map(connection => walls.get(connection.wallId))
    .filter((wall): wall is WallType => !!wall);
  if (
    participatingWalls.length !== 2 ||
    participatingWalls.some(wall => wall.lineForm === "arc")
  ) {
    return false;
  }

  const widthTolerance = 0.001;
  const firstWidth = participatingWalls[0].wallWidth ?? getFloorplannerStore().wallWidth;
  const firstLineStyle = participatingWalls[0].lineType ?? LineType.solid;
  return participatingWalls.every(wall =>
    Math.abs((wall.wallWidth ?? getFloorplannerStore().wallWidth) - firstWidth) <= widthTolerance &&
    (wall.lineType ?? LineType.solid) === firstLineStyle
  );
}

// ---------------------------------------------------------------------------
// Exported geometry utilities (kept for tests and other consumers)
// ---------------------------------------------------------------------------

/**
 * Sort vertices in clockwise order around a center point.
 * Three.js uses clockwise winding for front faces; sorting ascending by angle
 * gives counter-clockwise order (Y-up), so we reverse for clockwise.
 */
export function sortVerticesClockwise(
  vertices: THREE.Vector2[],
  center: THREE.Vector2
): THREE.Vector2[] {
  if (vertices.length <= 1) {
    return vertices;
  }

  const verticesWithAngles = vertices.map((vertex, index) => {
    const angle = Math.atan2(vertex.y - center.y, vertex.x - center.x);
    return { vertex, angle, index };
  });

  verticesWithAngles.sort((a, b) => a.angle - b.angle);
  return verticesWithAngles.reverse().map(item => item.vertex);
}

/**
 * Calculate the centroid (geometric center) of a set of points.
 */
export function calculateJointCenter(edgePoints: THREE.Vector2[]): THREE.Vector2 {
  if (edgePoints.length === 0) {
    return new THREE.Vector2(0, 0);
  }

  let sumX = 0;
  let sumY = 0;
  for (const point of edgePoints) {
    sumX += point.x;
    sumY += point.y;
  }

  return new THREE.Vector2(sumX / edgePoints.length, sumY / edgePoints.length);
}

/**
 * Collect all edge connection points that participate in a joint.
 * Retained for tests and edge-connection diagnostics.
 */
export function collectEdgePointsFromJoint(
  joint: JointInfo,
  walls: Map<string, WallType>
): THREE.Vector2[] {
  const edgePoints: THREE.Vector2[] = [];
  const connectedWalls = joint.connectedWalls || [];

  for (const connectionInfo of connectedWalls) {
    const wall = walls.get(connectionInfo.wallId);
    if (!wall || !wall.edgeConnections) {
      continue;
    }

    const wallEdgeConnections = wall.edgeConnections.filter(ec => {
      return (
        (ec.sourceWallId === connectionInfo.wallId &&
          ec.sourceEndpoint === connectionInfo.connectionPosition) ||
        (ec.targetWallId === connectionInfo.wallId &&
          ec.targetEndpoint === connectionInfo.connectionPosition)
      );
    });

    for (const edgeConnection of wallEdgeConnections) {
      const sourcePoint = edgeConnection.sourceEdgePoint instanceof THREE.Vector2
        ? edgeConnection.sourceEdgePoint
        : new THREE.Vector2(
            (edgeConnection.sourceEdgePoint as any).x,
            (edgeConnection.sourceEdgePoint as any).y
          );

      const targetPoint = edgeConnection.targetEdgePoint instanceof THREE.Vector2
        ? edgeConnection.targetEdgePoint
        : new THREE.Vector2(
            (edgeConnection.targetEdgePoint as any).x,
            (edgeConnection.targetEdgePoint as any).y
          );

      // PROXIMITY_TOLERANCE: edge points must be near the joint center to belong
      // to this joint (prevents pulling in points from a wall's other endpoint).
      const PROXIMITY_TOLERANCE = 0.01;
      if (sourcePoint.distanceTo(joint.point) <= PROXIMITY_TOLERANCE) {
        edgePoints.push(sourcePoint);
      }
      if (targetPoint.distanceTo(joint.point) <= PROXIMITY_TOLERANCE) {
        edgePoints.push(targetPoint);
      }
    }
  }

  return deduplicatePoints(edgePoints, 0.001);
}

/**
 * Remove duplicate points within a tolerance.
 */
export function deduplicatePoints(
  points: THREE.Vector2[],
  tolerance: number = 0.001
): THREE.Vector2[] {
  const result: THREE.Vector2[] = [];
  const toleranceSquared = tolerance * tolerance;

  for (const point of points) {
    const isDuplicate = result.some(existingPoint => {
      const dx = existingPoint.x - point.x;
      const dy = existingPoint.y - point.y;
      return (dx * dx + dy * dy) <= toleranceSquared;
    });

    if (!isDuplicate) {
      result.push(point);
    }
  }

  return result;
}

/**
 * Validate that a polygon (vertex ring) can be rendered: >= 3 vertices, finite
 * coordinates, non-zero area, not all collinear.
 */
export function validateJointPolygon(polygon: JointPolygonData): boolean {
  if (polygon.vertices.length < 3) {
    return false;
  }

  for (const vertex of polygon.vertices) {
    if (isNaN(vertex.x) || isNaN(vertex.y) || !isFinite(vertex.x) || !isFinite(vertex.y)) {
      return false;
    }
  }

  const area = calculatePolygonArea(polygon.vertices);
  if (Math.abs(area) < 0.0001) {
    return false;
  }

  if (arePointsCollinear(polygon.vertices)) {
    return false;
  }

  return true;
}

/**
 * Signed polygon area (shoelace). Positive = CCW, negative = CW (Y-up).
 */
export function calculatePolygonArea(vertices: THREE.Vector2[]): number {
  let area = 0;
  const n = vertices.length;

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += vertices[i].x * vertices[j].y;
    area -= vertices[j].x * vertices[i].y;
  }

  return area / 2;
}

/**
 * Check if all points in an array are collinear.
 */
export function arePointsCollinear(points: THREE.Vector2[]): boolean {
  if (points.length <= 2) {
    return true;
  }

  const tolerance = 0.0001;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      for (let k = j + 1; k < points.length; k++) {
        const p1 = points[i];
        const p2 = points[j];
        const p3 = points[k];
        const crossProduct =
          (p2.x - p1.x) * (p3.y - p1.y) -
          (p2.y - p1.y) * (p3.x - p1.x);
        if (Math.abs(crossProduct) >= tolerance) {
          return false;
        }
      }
    }
  }

  return true;
}
